import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSubtasks } from '@/hooks/useSubtasks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckSquare, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TaskSubtasksSectionProps {
  taskId: string;
  readOnly?: boolean;
}

export const TaskSubtasksSection = ({ taskId, readOnly = false }: TaskSubtasksSectionProps) => {
  const { subtasks, isLoading, createSubtask, isCreating, toggleSubtask, deleteSubtask } =
    useSubtasks(taskId);
  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const completedCount = subtasks.filter((s) => s.status === 'completed').length;

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      await createSubtask(newTitle.trim());
      setNewTitle('');
      setIsAdding(false);
    } catch (err) {
      console.error('Failed to create subtask:', err);
    }
  };

  if (isLoading) return null;

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <CheckSquare className="h-4 w-4 text-muted-foreground" />
          <span>Subtarefas</span>
          {subtasks.length > 0 && (
            <span className="text-xs text-muted-foreground">
              ({completedCount}/{subtasks.length})
            </span>
          )}
        </div>
        {!readOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => setIsAdding(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            Adicionar
          </Button>
        )}
      </div>

      {/* Progress bar */}
      {subtasks.length > 0 && (
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-emerald-500 rounded-full transition-all duration-300"
            style={{ width: `${(completedCount / subtasks.length) * 100}%` }}
          />
        </div>
      )}

      {/* Subtask list */}
      {subtasks.length > 0 && (
        <div className="space-y-0.5">
          {subtasks.map((subtask) => (
            <div
              key={subtask.id}
              className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/40 group"
            >
              <button
                className={cn(
                  'h-4 w-4 rounded-full border-2 flex-shrink-0 transition-colors',
                  subtask.status === 'completed'
                    ? 'bg-emerald-500 border-emerald-500'
                    : 'border-muted-foreground/30 hover:border-emerald-500'
                )}
                onClick={() => toggleSubtask({ id: subtask.id, currentStatus: subtask.status })}
              />
              <span
                className={cn(
                  'text-sm flex-1 min-w-0 truncate',
                  subtask.status === 'completed' && 'line-through text-muted-foreground'
                )}
              >
                {subtask.title}
              </span>
              {subtask.due_date && (
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {format(new Date(subtask.due_date + 'T12:00:00'), 'dd/MM', { locale: ptBR })}
                </span>
              )}
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 hover:text-destructive flex-shrink-0"
                  onClick={() => deleteSubtask(subtask.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick add form */}
      {isAdding && (
        <div className="flex items-center gap-2 mt-1">
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Nome da subtarefa..."
            className="h-8 text-sm flex-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newTitle.trim()) handleCreate();
              if (e.key === 'Escape') { setNewTitle(''); setIsAdding(false); }
            }}
          />
          <Button
            size="sm"
            className="h-8 text-xs"
            disabled={!newTitle.trim() || isCreating}
            onClick={handleCreate}
          >
            Salvar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => { setNewTitle(''); setIsAdding(false); }}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};
